import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Chip, Flex, Stack, Switch, Text, TextInput } from "@mantine/core";
import { AppModal } from "./AppModal";
import { useForm, yupResolver } from "@mantine/form";
import { useState } from "react";
import { useExportDataMutation } from "../graphql/graphql";
import { downloadCSV } from "../utils/File.utils";
import { showErrorNotification } from "./Notifications";
import { ExportDataInitialValues, ExportDataSchema } from "./ExportDataModal.validation";
export const ExportDataModal = ({ opened, close }) => {
    // State
    const [filename, setFilename] = useState(null);
    const [filenameError, setFilenameError] = useState(null);
    const [exportSelectionError, setExportSelectionError] = useState(null);
    // Mutations
    const [{ fetching: exportingData }, exportData] = useExportDataMutation();
    // Form
    const form = useForm({
        initialValues: ExportDataInitialValues,
        validate: yupResolver(ExportDataSchema)
    });
    // Handles exporting users
    const handleExportData = async () => {
        if (!filename) {
            setFilenameError("Please enter a filename.");
            return;
        }
        if (!form.values.exportOrganizations &&
            !form.values.exportOrganizationAdmins &&
            !form.values.exportProviders &&
            !form.values.exportCaseManagers &&
            !form.values.exportCaregivers &&
            !form.values.exportLocations) {
            setExportSelectionError("Please select at least one option to export.");
            return;
        }
        const valid = form.validate();
        if (valid.hasErrors)
            return;
        const { data, error } = await exportData({ ...form.values });
        if (error) {
            showErrorNotification("Failed to export data.");
            return;
        }
        // If we have data, download the CSV
        if (data?.data?.exportData) {
            try {
                const exportingOrganizations = form.values.exportOrganizations;
                // Sanitize the data to prevent circular references
                const sanitizedData = data.data.exportData.map((data) => {
                    const id = data?.id ?? "";
                    const organizationId = data?.organizationId ?? "";
                    const includeOrgMeta = exportingOrganizations &&
                        Boolean(id) &&
                        Boolean(organizationId) &&
                        id === organizationId;
                    return {
                        id,
                        organizationId: includeOrgMeta
                            ? organizationId
                            : "",
                        organizationName: data?.organizationName ?? "",
                        organizationTypes: includeOrgMeta
                            ? (data?.organizationTypes ?? "")
                            : "",
                        numberOfProviders: includeOrgMeta
                            ? (data?.numberOfProviders ?? "")
                            : "",
                        name: data?.name ?? "",
                        email: data?.email ?? "",
                        phoneNumber: data?.phoneNumber ?? "",
                        active: data?.active ?? "",
                        termsAndConditions: data?.termsAndConditions ?? "",
                        address1: data?.address1 ?? "",
                        address2: data?.address2 ?? "",
                        city: data?.city ?? "",
                        state: data?.state ?? "",
                        postalCode: data?.postalCode ?? ""
                    };
                });
                const columns = [
                    { key: "id", header: "ID" },
                    ...(exportingOrganizations
                        ? [
                            {
                                key: "organizationId",
                                header: "Organization ID"
                            },
                            {
                                key: "organizationTypes",
                                header: "Organization Types"
                            },
                            {
                                key: "numberOfProviders",
                                header: "Number of Providers"
                            }
                        ]
                        : []),
                    {
                        key: "organizationName",
                        header: "Organization Name"
                    },
                    { key: "name", header: "Name" },
                    { key: "email", header: "Email" },
                    { key: "phoneNumber", header: "Phone Number" },
                    { key: "active", header: "Active" },
                    {
                        key: "termsAndConditions",
                        header: "Terms and Conditions"
                    },
                    { key: "address1", header: "Address 1" },
                    { key: "address2", header: "Address 2" },
                    { key: "city", header: "City" },
                    { key: "state", header: "State" },
                    { key: "postalCode", header: "Zip Code" }
                ];
                downloadCSV(sanitizedData, columns, filename);
                handleClose();
            }
            catch (downloadError) {
                console.error("Download error:", downloadError);
                showErrorNotification("Failed to download CSV file.");
            }
        }
        else {
            showErrorNotification("No data received to export.");
        }
    };
    const handleClose = () => {
        setFilename(null);
        setFilenameError(null);
        setExportSelectionError(null);
        form.reset();
        close();
    };
    return (_jsx(AppModal, { title: "Export Data", opened: opened, onClose: handleClose, children: _jsxs(Stack, { mt: 10, gap: "md", children: [_jsx(TextInput, { autoFocus: true, required: true, label: "Filename", placeholder: "Filename", value: filename ?? "", error: filenameError, onChange: (event) => setFilename(event.currentTarget.value) }), _jsx(Switch, { defaultChecked: true, label: "Export Organizations", styles: form.values.exportOrganizations
                        ? {
                            track: {
                                backgroundColor: "var(--mantine-color-primary-9)"
                            }
                        }
                        : undefined, ...form.getInputProps("exportOrganizations") }), _jsx(Switch, { defaultChecked: true, label: "Export Organization Admins", styles: form.values.exportOrganizationAdmins
                        ? {
                            track: {
                                backgroundColor: "var(--mantine-color-primary-9)"
                            }
                        }
                        : undefined, ...form.getInputProps("exportOrganizationAdmins") }), _jsx(Switch, { defaultChecked: true, label: "Export Providers", styles: form.values.exportProviders
                        ? {
                            track: {
                                backgroundColor: "var(--mantine-color-primary-9)"
                            }
                        }
                        : undefined, ...form.getInputProps("exportProviders") }), _jsx(Switch, { defaultChecked: true, label: "Export Coordinators", styles: form.values.exportCaseManagers
                        ? {
                            track: {
                                backgroundColor: "var(--mantine-color-primary-9)"
                            }
                        }
                        : undefined, ...form.getInputProps("exportCaseManagers") }), _jsx(Switch, { label: "Export Locations", styles: form.values.exportLocations
                        ? {
                            track: {
                                backgroundColor: "var(--mantine-color-primary-9)"
                            }
                        }
                        : undefined, ...form.getInputProps("exportLocations") }), _jsx(Switch, { label: "Export Primary Contacts", styles: form.values.exportCaregivers
                        ? {
                            track: {
                                backgroundColor: "var(--mantine-color-red-5)"
                            }
                        }
                        : undefined, ...form.getInputProps("exportCaregivers") }), exportSelectionError && (_jsx(Text, { variant: "body", size: "sm", c: "var(--mantine-color-red-5)", mt: "xs", children: exportSelectionError })), _jsxs(Flex, { gap: "sm", mt: "md", children: [_jsx(Chip, { defaultChecked: true, styles: {
                                label: form.values.exportPhoneNumber
                                    ? {
                                        backgroundColor: "var(--mantine-color-primary-9)"
                                    }
                                    : undefined
                            }, ...form.getInputProps("exportPhoneNumber"), children: "Phone Number" }), _jsx(Chip, { defaultChecked: true, styles: {
                                label: form.values.exportEmail
                                    ? {
                                        backgroundColor: "var(--mantine-color-primary-9)"
                                    }
                                    : undefined
                            }, ...form.getInputProps("exportEmail"), children: "Email" })] }), _jsx(Flex, { justify: "flex-end", children: _jsx(Button, { variant: "filled", loading: exportingData, onClick: handleExportData, children: "Export" }) })] }) }));
};
